import { motion } from "framer-motion";
import { Star, Briefcase, User, Scale, Building2, Calculator } from "lucide-react";

const segments = [
  {
    icon: Briefcase,
    title: "Entrepreneurs",
    body:
      "Confiez-nous vos annonces légales, dépôts et registres pendant que vous développez votre activité. Vous gardez un interlocuteur unique et un calendrier maîtrisé.",
    cta: "Gagner du temps",
  },
  {
    icon: User,
    title: "Dirigeants d'entreprises",
    body:
      "Déléguez les actes chronophages — modifications, comptes annuels, registres — pour réduire les délais et sécuriser chaque échéance juridique.",
    cta: "Déléguer un acte",
  },
  {
    icon: Scale,
    title: "Cabinets d'avocats",
    body:
      "Confiez-nous les formalités juridiques et les actes chronophages de vos clients. Vous conservez la relation client et la direction du dossier ; nous exécutons avec traçabilité.",
    cta: "Confier un dossier",
  },
  {
    icon: Building2,
    title: "Directions juridiques",
    body:
      "Externalisez les volumes récurrents sans alourdir vos équipes. Nous préparons, contrôlons et suivons chaque formalité jusqu'à confirmation.",
    cta: "Externaliser un flux",
  },
  {
    icon: Calculator,
    title: "Experts-comptables",
    body:
      "Offrez à vos clients un relais juridique fiable sans recruter. Nous traitons les annonces, dépôts et registres en coordination avec votre cabinet.",
    cta: "Devenir relais",
  },
];

const avis = [
  {
    name: "Fulghesu",
    meta: "Gérant d'une SARL de BTP — Paris",
    service: "Formalités juridiques",
    date: "Mars 2026",
    stars: 5,
    text: "Statuts rédigés, annonce légale publiée, immatriculation au greffe — je n'ai eu rien à gérer. Tout était clair dès le devis. Je recommande vivement.",
  },
  {
    name: "Chantal Harribey",
    meta: "Fondatrice d'une agence web — Boulogne",
    service: "Conciliation",
    date: "Février 2026",
    stars: 5,
    text: "Après deux exercices difficiles, la conciliation ouverte par ProSecours nous a permis de rééchelonner notre dette URSSAF. La banque est restée à nos côtés. Un vrai soulagement.",
  },
  {
    name: "Anonyme",
    meta: "Associée d'une SCI familiale — Versailles",
    service: "Registres dématérialisés",
    date: "Avril 2026",
    stars: 4,
    text: "Modification de statuts et transmission de parts gérées avec une précision remarquable. Chaque étape expliquée simplement, sans jargon. On se sent accompagné du début à la fin.",
  },
];

const Stars = ({ n }) => (
  <span className="flex items-center gap-1 text-[#ce1e20]">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        size={15}
        strokeWidth={0}
        fill={i < n ? "#ce1e20" : "#d8d3c8"}
      />
    ))}
  </span>
);

export default function AvisClients() {
  return (
    <section
      id="clients"
      data-testid="clients-section"
      className="border-t border-[#1a1918]/15 bg-[#efece5] py-24 md:py-40"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="mb-14 grid grid-cols-12 md:mb-20"
        >
          <div className="col-span-12 md:col-span-3">
            <span data-testid="clients-kicker" className="section-badge">§ Nos clients</span>
          </div>
          <div className="col-span-12 mt-4 md:col-span-8 md:col-start-4 md:mt-0">
            <h2 className="font-serif-ed text-5xl leading-[1] tracking-tight text-[#1a1918] md:text-6xl lg:text-7xl">
              Un interlocuteur pour chaque{" "}
              <span className="italic text-[#ce1e20]">acteur du dossier</span>.
            </h2>
            <p className="mt-8 max-w-2xl text-base text-[#1a1918]/75 md:text-lg">
              Confiez à ProSecours les formalités juridiques et les actes
              chronophages qui ralentissent vos équipes. Chaque client garde la
              maîtrise de son dossier ; nous exécutons, suivons et confirmons
              chaque étape.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {segments.map((segment, i) => {
            const Icon = segment.icon;
            return (
              <motion.article
                key={segment.title}
                data-testid={`client-segment-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.06 }}
                className="bg-white p-6 shadow-sm"
              >
                <span className="mb-6 flex h-11 w-11 items-center justify-center bg-[#0a1e38] text-white">
                  <Icon size={19} strokeWidth={1.5} />
                </span>
                <h3 className="font-serif-ed text-2xl italic text-[#1a1918]">
                  {segment.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#1a1918]/70">
                  {segment.body}
                </p>
                <a
                  href="#audit"
                  data-testid={`client-segment-cta-${i}`}
                  className="mt-6 inline-flex items-center gap-2 overline text-[#ce1e20] transition-colors hover:text-[#a71518]"
                >
                  {segment.cta}
                  <span>→</span>
                </a>
              </motion.article>
            );
          })}
        </div>

        <div className="mt-20 md:mt-28">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
            <h3 className="font-serif-ed text-4xl leading-tight text-[#1a1918] md:text-5xl">
              Ils nous font confiance.
            </h3>
            <div
              data-testid="avis-summary"
              className="flex flex-wrap items-center gap-4 md:gap-6"
            >
              <span className="font-serif-ed text-4xl text-[#1a1918] md:text-5xl">
                4,7/5
              </span>
              <Stars n={5} />
              <span className="hidden overline text-[#1a1918]/40 md:inline">
                Paris & Île-de-France
              </span>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-y-12 md:gap-x-8">
            {avis.map((a, i) => (
              <motion.figure
                key={a.name}
                data-testid={`avis-card-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: (i % 3) * 0.1 }}
                className="col-span-12 flex flex-col bg-white p-7 shadow-sm md:col-span-6 lg:col-span-4"
              >
                <div className="flex items-center justify-between">
                  <Stars n={a.stars} />
                  <span className="overline text-[#1a1918]/45">{a.date}</span>
                </div>
                <blockquote className="mt-5 flex-1 text-base leading-relaxed text-[#1a1918]/85 md:text-lg">
                  {a.text}
                </blockquote>
                <figcaption className="mt-7">
                  <div className="font-serif-ed text-xl text-[#1a1918]">{a.name}</div>
                  <div className="mt-1 text-sm text-[#1a1918]/65">{a.meta}</div>
                  <div className="mt-2 overline text-[#ce1e20]">{a.service}</div>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
